import { activePage } from "../native/pages";
import type { EngineDocument, SceneNode } from "../scene/model";
import type {
  WorkerRequest,
  WorkerResponse,
  RenderJob,
} from "./native-worker-protocol";
interface Job {
  id: number;
  job: RenderJob;
  inputs: Map<string, HTMLImageElement>;
  resolve: (value: WorkerResponse) => void;
  reject: (error: Error) => void;
}
/** Materialize sources only for the running job, with a bounded reusable worker pool. */
export class NativeRenderWorker {
  private worker: Worker | undefined;
  private failed = false;
  private epoch = 0;
  private serial = 0;
  private encodedResizes = 0;
  private fullTransfers = 0;
  private images = new Map<HTMLImageElement, { key: string; bytes: number }>();
  private sourceBytes = 0;
  private readonly sourceBudget = 64 * 1024 * 1024;
  private queue = new Map<string, Job>();
  private active: Job | undefined;
  available() {
    return (
      !this.failed &&
      typeof Worker !== "undefined" &&
      typeof OffscreenCanvas !== "undefined" &&
      typeof createImageBitmap !== "undefined"
    );
  }
  private start() {
    if (!this.worker) {
      try {
        this.worker = new Worker(
          "/templates/boxed/controllers/creatorenginepro/assets/enginepro/native-raster-worker-v77.js",
          { type: "module" },
        );
      } catch (error) {
        this.failed = true;
        throw error;
      }
      const worker = this.worker,
        epoch = this.epoch;
      worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
        if (this.worker !== worker || this.epoch !== epoch) {
          data.image?.close();
          return;
        }
        const current = this.active;
        if (current?.id === data.id) {
          this.active = undefined;
          if (data.error) current.reject(new Error(data.error));
          else current.resolve(data);
        } else data.image?.close();
        this.trimSources(new Set());
        this.pump();
      };
      worker.onerror = () => {
        if (this.worker === worker && this.epoch === epoch) {
          this.failed = true;
          this.reset();
        }
      };
    }
    return this.worker;
  }
  private trimSources(keep: Set<HTMLImageElement>) {
    for (const [image, entry] of this.images) {
      if (this.sourceBytes <= this.sourceBudget) break;
      if (keep.has(image)) continue;
      this.images.delete(image);
      this.sourceBytes -= entry.bytes;
      const message: WorkerRequest = { type: "release", source: entry.key };
      this.worker?.postMessage(message);
    }
  }
  private async image(
    source: HTMLImageElement,
    keep: Set<HTMLImageElement>,
    epoch: number,
  ) {
    const cached = this.images.get(source);
    if (cached) {
      this.images.delete(source);
      this.images.set(source, cached);
      return cached.key;
    }
    const worker = this.start(),
      key = "image-" + ++this.serial;
    const image = await createImageBitmap(source, { premultiplyAlpha: "none" });
    if (epoch !== this.epoch) {
      image.close();
      throw new Error("Obsolete render source");
    }
    const bytes = image.width * image.height * 4;
    this.images.set(source, { key, bytes });
    this.fullTransfers++;
    this.sourceBytes += bytes;
    this.trimSources(keep);
    const message: WorkerRequest = { type: "image", source: key, image };
    worker.postMessage(message, [image]);
    return key;
  }
  private submit(
    key: string,
    job: RenderJob,
    inputs: Map<string, HTMLImageElement>,
  ) {
    return new Promise<WorkerResponse>((resolve, reject) => {
      this.queue.get(key)?.reject(new Error("Superseded render job"));
      this.queue.set(key, { id: ++this.serial, job, inputs, resolve, reject });
      this.pump();
    });
  }
  private pump() {
    if (this.active || !this.queue.size) return;
    const [key, entry] = this.queue.entries().next().value!;
    this.queue.delete(key);
    this.active = entry;
    const epoch = this.epoch;
    void (async () => {
      const names = new Map<string, string>(),
        keep = new Set(entry.inputs.values());
      let encoded: Blob | undefined;
      if (entry.job.type === "resize") {
        // A display level does not require a full-resolution transferable bitmap.
        // Decode encoded bytes directly to the requested size inside the worker.
        const source = entry.inputs.get(entry.job.source)!;
        try {
          const response = await fetch(source.currentSrc || source.src, {
            credentials: "same-origin",
          });
          if (!response.ok) throw new Error("Image source unavailable");
          encoded = await response.blob();
          this.encodedResizes++;
        } catch {
          /* HTMLImage CORS/decoder fallback retains the precise path. */
        }
      }
      if (!encoded)
        for (const [name, source] of entry.inputs)
          names.set(name, await this.image(source, keep, epoch));
      if (epoch !== this.epoch) return;
      const original = entry.job;
      const job: RenderJob =
        original.type === "resize"
          ? {
              ...original,
              source: names.get(original.source) ?? original.source,
              ...(encoded ? { encoded } : {}),
            }
          : {
              ...original,
              document: {
                ...original.document,
                assets: original.document.assets.map((a) => ({
                  ...a,
                  source: names.get(a.source)!,
                })),
              },
            };
      const message: WorkerRequest = { type: "job", id: entry.id, job };
      this.start().postMessage(message);
    })().catch((error) => {
      if (this.active === entry) {
        this.active = undefined;
        entry.reject(error);
        this.trimSources(new Set());
        this.pump();
      }
    });
  }
  async resize(image: HTMLImageElement, width: number, height: number) {
    const key = "resize-" + image.src + "-" + width + "-" + height;
    return (
      await this.submit(
        key,
        { type: "resize", source: "input", width, height },
        new Map([["input", image]]),
      )
    ).image!;
  }
  raster(
    document: EngineDocument,
    node: SceneNode,
    base: SceneNode | undefined,
    density: number,
    get: (source: string) => HTMLImageElement | undefined,
  ) {
    const nodes = base ? [base, node] : [node],
      used = new Set(nodes.flatMap((n) => Object.values(n.media))),
      inputs = new Map<string, HTMLImageElement>();
    const assets = document.assets
      .filter((a) => a.kind === "image" && used.has(a.id))
      .map((a) => {
        const image = get(a.source);
        if (image) inputs.set(a.id, image);
        return { ...a, source: a.id };
      });
    if (inputs.size !== assets.length)
      return Promise.reject(new Error("Missing raster source"));
    const scene = {
      ...document,
      assets,
      pages: [{ ...activePage(document), nodes }],
    };
    return this.submit(
      "raster-" + node.id,
      { type: "raster", document: scene, nodeId: node.id, density },
      inputs,
    );
  }
  reset() {
    this.epoch++;
    this.worker?.terminate();
    this.worker = undefined;
    this.images.clear();
    this.sourceBytes = 0;
    const error = new Error("Render context replaced");
    this.active?.reject(error);
    this.active = undefined;
    for (const entry of this.queue.values()) entry.reject(error);
    this.queue.clear();
  }
  cancelRaster(nodeId: string) {
    const key = "raster-" + nodeId;
    this.queue.get(key)?.reject(new Error("Raster no longer visible"));
    this.queue.delete(key);
  }
  stats() {
    return {
      pending: this.queue.size + Number(!!this.active),
      sources: this.images.size,
      sourceRgbaBytes: this.sourceBytes,
      sourceBudget: this.sourceBudget,
      encodedResizes: this.encodedResizes,
      fullTransfers: this.fullTransfers,
      available: this.available(),
    };
  }
}
